import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
	Conversation,
	fetchConversations,
	getDisplayName,
} from "@/lib/messaging";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";

interface ConversationListProps {
	onSelectConversation: (conversation: Conversation) => void;
	selectedConversationId?: string;
}

export function ConversationList({
	onSelectConversation,
	selectedConversationId,
}: ConversationListProps) {
	const { user } = useAuth();
	const [conversations, setConversations] = useState<Conversation[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		async function loadConversations() {
			if (!user) return;

			setLoading(true);
			const data = await fetchConversations(user.id);
			setConversations(data);
			setLoading(false);

			// Select first conversation if available and none is selected
			if (data.length > 0 && !selectedConversationId) {
				onSelectConversation(data[0]);
			}
		}

		loadConversations();

		// Set up two subscriptions: one for new messages, one for conversation updates
		if (user) {
			// Listen for new messages
			const messagesChannel = supabase
				.channel("messages-changes")
				.on(
					"postgres_changes",
					{
						event: "INSERT",
						schema: "public",
						table: "messages",
					},
					() => {
						// Refresh conversations when a new message is added
						console.log(
							"New message detected, refreshing conversations"
						);
						loadConversations();
					}
				)
				.subscribe();

			// Listen for conversation updates
			const conversationsChannel = supabase
				.channel("conversation-updates")
				.on(
					"postgres_changes",
					{
						event: "UPDATE",
						schema: "public",
						table: "conversations",
					},
					(payload) => {
						console.log("Conversation updated:", payload.new);
						loadConversations();
					}
				)
				.subscribe();

			return () => {
				supabase.removeChannel(messagesChannel);
				supabase.removeChannel(conversationsChannel);
			};
		}
	}, [user, onSelectConversation, selectedConversationId]);

	if (loading) {
		return (
			<div className="space-y-3">
				{[1, 2, 3].map((i) => (
					<Card key={i} className="cursor-pointer">
						<CardContent className="p-4">
							<div className="flex items-center space-x-4">
								<Skeleton className="h-10 w-10 rounded-full" />
								<div className="space-y-2 flex-1">
									<Skeleton className="h-4 w-2/3" />
									<Skeleton className="h-3 w-1/2" />
								</div>
							</div>
						</CardContent>
					</Card>
				))}
			</div>
		);
	}

	if (conversations.length === 0) {
		return (
			<div className="text-center py-8">
				<MessageSquare className="mx-auto h-12 w-12 text-gray-300 mb-3" />
				<h3 className="text-lg font-medium text-gray-900">
					No conversations yet
				</h3>
				<p className="text-gray-500 mt-1">
					Start a conversation with a supplier to see it here.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{conversations.map((conversation) => {
				const displayName = getDisplayName(
					conversation,
					user?.id || ""
				);
				const profileImage =
					conversation.retailer_id === user?.id
						? conversation.supplier_profile?.profile_image
						: conversation.retailer_profile?.profile_image;

				return (
					<Card
						key={conversation.id}
						className={`cursor-pointer transition-colors hover:bg-gray-50 ${
							selectedConversationId === conversation.id
								? "border-accent"
								: ""
						}`}
						onClick={() => onSelectConversation(conversation)}>
						<CardContent className="p-4">
							<div className="flex items-center space-x-4">
								<Avatar>
									<AvatarImage
										src={profileImage || undefined}
									/>
									<AvatarFallback className="bg-primary/10 text-primary">
										{displayName
											.substring(0, 2)
											.toUpperCase()}
									</AvatarFallback>
								</Avatar>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-gray-900 truncate">
										{displayName}
									</p>
									<p className="text-xs text-gray-500 truncate">
										{conversation.last_message ||
											"No messages yet"}
									</p>
								</div>
								<div className="text-xs text-gray-400">
									{conversation.last_message_at
										? formatDistanceToNow(
												new Date(
													conversation.last_message_at
												),
												{ addSuffix: true }
										  )
										: ""}
								</div>
							</div>
						</CardContent>
					</Card>
				);
			})}
		</div>
	);
}
